import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Toggle, LoadingState, ErrorState, ApiModeBanner } from "../components/AdminUI";
import { useAdminData } from "../../../store/adminDataStore";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import type { PortfolioItem } from "../../../store/types";

const NOW = () => new Date().toISOString();
type TabKey = "showreel" | "featured" | "all";

export default function AdminHomepagePage() {
  const { state, upsertPortfolioItem, status, refetchPortfolio, isApiMode } = useAdminData();
  const t = useAdminT();
  const { showToast } = useToast();
  const [tab, setTab] = useState<TabKey>("showreel");

  const allPublished = [...state.portfolioItems]
    .filter(p => p.is_published)
    .sort((a, b) => a.sort_order - b.sort_order);

  const showreelItems = allPublished.filter(p => p.is_showreel);
  const featuredItems = allPublished.filter(p => p.is_featured);
  const displayed = tab === "showreel" ? showreelItems : tab === "featured" ? featuredItems : allPublished;

  const toggle = async (item: PortfolioItem, field: "is_showreel" | "is_featured") => {
    try {
      await upsertPortfolioItem({ ...item, [field]: !item[field], updated_at: NOW() });
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    }
  };

  const changeMode = async (item: PortfolioItem, mode: typeof item.homepage_display_mode) => {
    try {
      await upsertPortfolioItem({ ...item, homepage_display_mode: mode, updated_at: NOW() });
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    }
  };

  const changeSort = async (item: PortfolioItem, sort: number) => {
    try {
      await upsertPortfolioItem({ ...item, sort_order: sort, updated_at: NOW() });
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    }
  };

  const DISPLAY_MODES = ["reel", "still", "both"] as const;
  const MODE_COLORS = {
    reel: "bg-[#C8B8A6]/20 text-[#C8B8A6]",
    still: "bg-[#D9D9D9]/60 text-[#2C2C2C]/50",
    both: "bg-emerald-50 text-emerald-600",
  };

  const TABS: Array<{ key: TabKey; label: string; count: number }> = [
    { key: "showreel", label: t("tabifotoAdmin.homepage.tab_showreel"), count: showreelItems.length },
    { key: "featured", label: t("tabifotoAdmin.homepage.tab_featured"), count: featuredItems.length },
    { key: "all", label: t("tabifotoAdmin.homepage.tab_all"), count: allPublished.length },
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.homepage.title")}
      subtitle={t("tabifotoAdmin.homepage.subtitle")}
    >
      <ApiModeBanner isApiMode={isApiMode} />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: t("tabifotoAdmin.homepage.on_showreel"), value: showreelItems.length, icon: "ri-film-line" },
          { label: t("tabifotoAdmin.homepage.featured_works"), value: featuredItems.length, icon: "ri-star-line" },
          { label: t("tabifotoAdmin.homepage.total_published"), value: allPublished.length, icon: "ri-gallery-line" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg border border-[#D9D9D9]/60 p-4 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center text-[#C8B8A6]">
              <i className={`${s.icon} text-xl`} />
            </div>
            <div>
              <p className="text-2xl font-extralight text-[#2C2C2C]">{s.value}</p>
              <p className="text-xs text-[#2C2C2C]/40">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {status.portfolio.loading && <LoadingState />}
      {status.portfolio.error && <ErrorState message={status.portfolio.error} onRetry={refetchPortfolio} />}

      {!status.portfolio.loading && !status.portfolio.error && (
        <>
          <div className="flex gap-2 mb-4">
            {TABS.map(tb => (
              <button key={tb.key} onClick={() => setTab(tb.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-light cursor-pointer whitespace-nowrap transition-colors ${tab === tb.key ? "bg-[#2C2C2C] text-white" : "bg-white border border-[#D9D9D9] text-[#2C2C2C]/60 hover:border-[#2C2C2C]/30"}`}>
                {tb.label} <span className="opacity-40 ml-1">{tb.count}</span>
              </button>
            ))}
          </div>

          {displayed.length === 0 ? (
            <div className="bg-white rounded-lg border border-[#D9D9D9]/60 py-16 text-center text-sm text-[#2C2C2C]/30">
              {t("tabifotoAdmin.homepage.publish_first")}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {displayed.map(item => (
                <div key={item.id} className="bg-white rounded-lg border border-[#D9D9D9]/60 overflow-hidden">
                  <div className="relative w-full h-36 bg-[#D9D9D9]/20 overflow-hidden">
                    {item.thumbnail_url && (
                      <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover object-top" loading="lazy" />
                    )}
                    <div className="absolute top-2 right-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-light ${MODE_COLORS[item.homepage_display_mode]}`}>
                        {item.homepage_display_mode}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-light text-[#2C2C2C] mb-1 truncate">{item.title}</p>
                    <p className="text-xs text-[#2C2C2C]/40 mb-3 capitalize">{item.city} · {item.shoot_type}</p>
                    <div className="space-y-2">
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-xs text-[#2C2C2C]/50 flex items-center gap-1.5">
                          <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-film-line text-[#C8B8A6]" /></div>
                          {t("tabifotoAdmin.homepage.is_showreel")}
                        </span>
                        <Toggle checked={item.is_showreel} onChange={() => toggle(item, "is_showreel")} />
                      </label>
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-xs text-[#2C2C2C]/50 flex items-center gap-1.5">
                          <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-star-line text-[#C8B8A6]" /></div>
                          {t("tabifotoAdmin.homepage.is_featured")}
                        </span>
                        <Toggle checked={item.is_featured} onChange={() => toggle(item, "is_featured")} />
                      </label>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#2C2C2C]/50">{t("tabifotoAdmin.homepage.display_mode")}</span>
                        <select className="text-xs border border-[#D9D9D9] rounded px-1.5 py-0.5 text-[#2C2C2C] bg-white focus:outline-none focus:border-[#C8B8A6] cursor-pointer"
                          value={item.homepage_display_mode}
                          onChange={e => changeMode(item, e.target.value as typeof item.homepage_display_mode)}>
                          {DISPLAY_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#2C2C2C]/50">{t("tabifotoAdmin.homepage.sort_order_field")}</span>
                        <input type="number" className="w-16 px-2 py-0.5 rounded border border-[#D9D9D9] text-xs text-center focus:outline-none focus:border-[#C8B8A6]"
                          value={item.sort_order}
                          onChange={e => changeSort(item, Number(e.target.value))} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
